import { Button, Card, Col, Image, message, Row } from "antd"
import * as API from "common/api"
import * as COMMONS from "common/common"
import "moment/locale/ja"
import React, { useState } from "react"
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd"
import { Helmet } from "react-helmet-async"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useNavigate } from "react-router-dom"
import { FaEye, FaEyeSlash } from "react-icons/fa"

const ProductsSort = (props) => {
  const navigate = useNavigate()

  const isMountedRef = COMMONS.USE_IS_MOUNTED_REF()
  const queryClient = useQueryClient()

  const [products, setProducts] = useState([])
  const [orderedProducts, setOrderedProducts] = useState([])
  const [categories, setCategories] = useState([])

  useQuery(
    [API.QUERY_KEY_ADMIN_CATEGORIES, {}],
    () => API.ADMIN_GET_CATEGORIES(),
    {
      onSuccess: (response) => {
        if (isMountedRef.current) {
          setCategories(response?.data || [])
        }
      },
      onError: (error) => {
        if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
          navigate(COMMONS.PERMISSION_ERROR_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
          message.warning(COMMONS.ERROR_SESSION_MSG)
          navigate(COMMONS.ADMIN_LOGIN_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        } else {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        }
      },
    }
  )

  useQuery(
    [API.QUERY_KEY_ADMIN_PRODUCTS_LIST, {}],
    () => API.ADMIN_GET_PRODUCTS_LIST(),
    {
      onSuccess: (response) => {
        if (isMountedRef.current) {
          setProducts(response?.data || [])
          setOrderedProducts(response?.data || [])
        }
      },
      onError: (error) => {
        if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
          navigate(COMMONS.PERMISSION_ERROR_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
          message.warning(COMMONS.ERROR_SESSION_MSG)
          navigate(COMMONS.ADMIN_LOGIN_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        } else {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        }
      },
    }
  )

  const updateProductOrderMutation = useMutation(
    API.ADMIN_UPDATE_PRODUCT_ORDER,
    {
      onSuccess: () => {
        if (isMountedRef.current) {
          message.success(COMMONS.SUCCESS_SAVE_MSG)
          queryClient.invalidateQueries(API.QUERY_KEY_ADMIN_PRODUCTS)
        }
      },
      onError: (error) => {
        if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
          navigate(COMMONS.PERMISSION_ERROR_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
          message.warning(COMMONS.ERROR_SESSION_MSG)
          navigate(COMMONS.ADMIN_LOGIN_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        } else {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        }
      },
    }
  )

  const handleUpdate = () => {
    const paramData = {
      products: orderedProducts.map((p, i) => ({
        productId: p?.productId,
        displayOrder: i,
      })),
    }

    updateProductOrderMutation.mutate(paramData)
  }

  const handleRevert = () => {
    setOrderedProducts(products)
  }

  const productOnDragEndHandle = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return
    } else {
      const reOrderedProducts = COMMONS.RE_ORDER(
        orderedProducts,
        result.source.index,
        result.destination.index
      )

      setOrderedProducts(reOrderedProducts)
    }
  }

  return (
    <>
      <Helmet>
        <title>商品表示順 | {process.env.REACT_APP_SYSTEM_NAME}</title>
      </Helmet>
      <Card
        title="商品表示順"
        className="animate__animated animate__fadeIn p-4"
        bordered={false}
        extra={
          <>
            <Button className="m-1" size="large" danger onClick={handleRevert}>
              元に戻す
            </Button>
            <Button
              type="primary"
              className="m-1 w-32"
              size="large"
              onClick={handleUpdate}
            >
              保存
            </Button>
          </>
        }
      >
        <div className="flex justify-center bg-secondary rounded mb-8 p-4">
          <p>商品を上下にドラッグして並べ替えます</p>
        </div>
        <Row gutter={[32, 32]}>
          <Col xs={24} md={12}>
            {orderedProducts && orderedProducts?.length > 0 ? (
              <DragDropContext onDragEnd={productOnDragEndHandle}>
                <Droppable droppableId="droppableProduct" direction="vertical">
                  {(provided, snapshot) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="flex flex-col"
                    >
                      {orderedProducts.map((product, index) => (
                        <Draggable
                          key={product?.productId + ""}
                          draggableId={product?.productId + ""}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`flex mb-4 cursor-pointer shadow-md bg-white rounded ${
                                product?.isDisplayed
                                  ? "shadow-orange-200"
                                  : "shadow-red-200"
                              }`}
                            >
                              <div className="flex items-center w-full">
                                {product?.productImages &&
                                product?.productImages?.length > 0 ? (
                                  <Image
                                    src={`${API.UPLOADS_URL}${
                                      product.productImages[0]?.picUrl || ""
                                    }`}
                                    preview={false}
                                    height={100}
                                    width={150}
                                    alt={product.productName || ""}
                                    className="rounded object-contain"
                                  />
                                ) : (
                                  <Image
                                    preview={false}
                                    src="/default.jpg"
                                    height={100}
                                    width={150}
                                    alt={product.productName || ""}
                                    className="rounded object-contain"
                                  />
                                )}
                                <p className="text-xs p-4">
                                  {product?.productName}
                                </p>
                              </div>
                              <div className="relative">
                                <div className="absolute top-2 right-2">
                                  {product?.isDisplayed ? (
                                    <FaEye className="text-primary text-lg" />
                                  ) : (
                                    <FaEyeSlash className="text-red-600 text-lg" />
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              <p className="text-center">商品がありません。</p>
            )}
          </Col>
          <Col xs={24} md={12}>
            <div className="flex flex-col bg-gray-50 p-4 rounded">
              <p className="font-bold text-xl text-primary mb-4">プレビュー</p>
              {categories && categories.length > 0 ? (
                categories.map((category) => (
                  <div className="flex-col" key={category?.categoryId}>
                    <p className="text-lg font-bold my-8">
                      {category?.categoryName || ""}
                    </p>
                    {orderedProducts && orderedProducts?.length > 0 ? (
                      orderedProducts.filter(
                        (p) => p?.categoryId === category?.categoryId
                      ).length > 0 ? (
                        <Row gutter={[16, 16]}>
                          {orderedProducts
                            .filter(
                              (p) => p?.categoryId === category?.categoryId
                            )
                            .filter((p) => p?.isDisplayed)
                            .map((product) => (
                              <Col xs={12} md={8} key={product?.productId}>
                                <div className="flex flex-col shadow-md rounded h-full bg-white">
                                  <div>
                                    <div className="flex justify-center">
                                      {product.productImages &&
                                      product.productImages.length > 0 ? (
                                        <Image
                                          src={`${API.UPLOADS_URL}${
                                            product.productImages[0]?.picUrl ||
                                            ""
                                          }`}
                                          preview={false}
                                          height={100}
                                          alt={product.productName || ""}
                                          className="rounded object-contain"
                                        />
                                      ) : (
                                        <Image
                                          preview={false}
                                          src="/default.jpg"
                                          height={100}
                                          alt={product.productName || ""}
                                          className="rounded object-contain"
                                        />
                                      )}
                                    </div>
                                  </div>
                                  <div className="p-2">
                                    <p className="text-xs text-center">
                                      {product.productName || "ー"}
                                    </p>
                                  </div>
                                </div>
                              </Col>
                            ))}
                        </Row>
                      ) : (
                        <p className="text-center">商品がありません。</p>
                      )
                    ) : (
                      <p className="text-center">商品がありません。</p>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-center">商品カテゴリーがありません。</p>
              )}
            </div>
          </Col>
        </Row>
      </Card>
    </>
  )
}

export default ProductsSort
